import { PAGES } from "@/constants";
import { employeeTasksAPI } from "./api";

export const EMPLOYEE_TASK_CERTIFICATE_TAB = "M__ProjectFormCertificate";

/**
 * Открывает целевую страницу в новой вкладке по типу задачи.
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function tryOpenEmployeeTaskComplete(row) {
  const code = row?.task_type_code;
  if (code === "sign_asr" || code === "sign_exec_schema") {
    const floorMapDocumentId = row.floor_map_document_id;
    if (floorMapDocumentId) 
      window.open(`${PAGES.unsignedSign}?tab=exec_docs&floor_map_document_id=${floorMapDocumentId}`, "_blank", "noopener,noreferrer");
    else
      window.open(PAGES.unsignedSign, "_blank", "noopener,noreferrer");
    return { ok: true };
  }
  if (code === "pto_check") {
    window.open(PAGES.ptoDocuments, "_blank", "noopener,noreferrer");
    return { ok: true };
  }
  if (code === "upload_certificate") {
    const {
      resident_id,
      project_type_id,
      project_entrance_id,
      project_id,
      sbs_project_id,
    } = row;
    const ids = {
      resident_id,
      project_type_id,
      project_entrance_id,
      project_id,
      sbs_project_id,
    };
    const missing = Object.entries(ids).filter(([, v]) => v == null || v === "");
    if (missing.length) {
      return {
        ok: false,
        message: "Недостаточно данных проекта для перехода к сертификатам",
      };
    }
    const q = new URLSearchParams({
      resident_id: String(resident_id),
      project_type_id: String(project_type_id),
      project_entrance_id: String(project_entrance_id),
      project_id: String(project_id),
      sbs_project_id: String(sbs_project_id),
      tab: EMPLOYEE_TASK_CERTIFICATE_TAB,
    });
    window.open(
      `${PAGES.residentialSettings}?${q.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
    return { ok: true };
  }
  if (code === 'upload_laboratory_act') {
    const {
      resident_id,
      project_type_id,
      project_entrance_id,
      project_id,
      sbs_project_id,
    } = row;
    const q = new URLSearchParams({
      resident_id: String(resident_id),
      project_type_id: String(project_type_id),
      project_entrance_id: String(project_entrance_id),
      project_id: String(project_id),
      sbs_project_id: String(sbs_project_id),
      tab: EMPLOYEE_TASK_CERTIFICATE_TAB,
      certificate_tab: 'laboratory_acts'
    });
    window.open(
      `${PAGES.residentialSettings}?${q.toString()}`,
      "_blank",
      "noopener,noreferrer"
    );
    return { ok: true };
  }
  return {
    ok: false,
    message: "Для этого типа задачи переход не настроен",
  };
}

/**
 * Достаёт work_sets для пары placement_type + work_set_check_group из ответа getFloorWorkSets (как в WorkSets.vue).
 */
export function pickWorkSetsFromFloorInfo(info, placementTypeId, workSetCheckGroupId) {
  if (placementTypeId == null || workSetCheckGroupId == null) return [];
  const types = info?.data;
  if (!Array.isArray(types)) return [];
  const type = types.find((x) => Number(x.placement_type_id) === Number(placementTypeId));
  const groups = type?.work_set_check_groups;
  if (!Array.isArray(groups)) return [];
  const group = groups.find(
    (g) => Number(g.work_set_check_group_id) === Number(workSetCheckGroupId)
  );
  return Array.isArray(group?.work_sets) ? group.work_sets?.map(item => ({...item, placement_type_id: placementTypeId})) : [];
}

/**
 * Все строки работ группы вызова по каждому типу помещения на этаже.
 * Один и тот же work_set_id может повторяться с разными placement_type_id — отдельные объёмы в модалке/PDF.
 */
export function buildExecListModalRowsForGroup(info, workSetCheckGroupId) {
  if (workSetCheckGroupId == null) return [];
  const types = info?.data;
  if (!Array.isArray(types)) return [];
  const rows = [];
  for (const t of types) {
    const groups = t?.work_set_check_groups;
    if (!Array.isArray(groups)) continue;
    const group = groups.find(
      (g) => Number(g.work_set_check_group_id) === Number(workSetCheckGroupId)
    );
    const work_sets = group?.work_sets;
    if (!Array.isArray(work_sets) || !work_sets.length) continue;
    const placement_type_id = t.placement_type_id;
    const placement_type_name = t.placement_type_name || "";
    for (const s of work_sets) {
      rows.push({
        placement_type_id,
        placement_type_name,
        work_set_id: s.work_set_id,
        work_set_name: s.work_set_name,
        unit_name: s.unit_name,
        work_set_amount: s.work_set_amount,
      });
    }
  }
  return rows;
}

/**
 * Ответ public.exec_list_work_sets_for_floor_map: плоские строки → строки модалки исп. схемы.
 * placement_type_id — как в мастере / вызовах (setting); legend — для подписи и уникального rowKey.
 */
export function buildExecListModalRowsFromScreedPlacementRows(rows, workSetCheckGroupId) {
  if (workSetCheckGroupId == null || !Array.isArray(rows)) return [];
  return rows
    .filter((r) => Number(r.work_set_check_group_id) === Number(workSetCheckGroupId))
    .map((r) => {
      const legendName = String(r.legend_placement_type_name ?? "").trim();
      const settingName = String(r.setting_placement_type_name ?? "").trim();
      const placement_type_name = legendName || settingName;
      return {
        placement_type_id: r.setting_placement_type_id,
        legend_placement_type_id: r.legend_placement_type_id,
        placement_type_name,
        work_set_id: r.work_set_id,
        work_set_name: r.work_set_name,
        unit_name: r.unit_name,
        work_set_amount: r.work_set_amount,
      };
    });
}

export function formatTaskRowversion(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const employeeTaskExecutionFilterOptions = [
  { label: "Все", value: "all" },
  { label: "Выполнено", value: "done" },
  { label: "Не выполнено", value: "open" },
];

export const employeeTasksColumns = [
  { name: "employee_task_id", label: "ID", align: "center", field: "employee_task_id" },
  { name: "task_type_code", label: "Тип задачи", align: "left", field: "task_type_code" },
  { name: "resident_name", label: "ЖК", align: "left", field: "resident_name" },
  { name: "project_type_name", label: "Тип проекта", align: "left", field: "project_type_name" },
  {
    name: "entrance_floor",
    label: "Подъезд / этаж",
    align: "center",
    field: "entrance",
  },
  { name: "work_set_check_group_name", label: "Группа работ", align: "left", field: "work_set_check_group_name" },
  { name: "placement_type_name", label: "Тип помещения", align: "left", field: "placement_type_name" },
  { name: "rowversion", label: "Создана", align: "center", field: "rowversion" },
  { name: "is_done", label: "Статус", align: "center", field: "is_done" },
  { name: "actions", label: "", align: "center", field: "actions" },
];

export const getEmployeeTasks = async (executionFilter = "all") => {
  try {
    const params = {};
    if (executionFilter === "done") {
      params.is_done = true;
    } else if (executionFilter === "open") {
      params.is_done = false;
    }
    const res = await employeeTasksAPI.read(params);
    const list = res?.data;
    return list || [];
  } catch (e) {
    return null;
  }
};

export const setEmployeeTaskIsDone = async (employee_task_id, is_done) => {
  try {
    const res = await employeeTasksAPI.setIsDone({
      employee_task_id,
      is_done,
    });
    return res ?? null;
  } catch (e) {
    return null;
  }
};

export const getSchemaIdByWorkSetCheckGroup = async (workSetCheckGroupId, projectId = null) => {
  if (workSetCheckGroupId == null || workSetCheckGroupId === "") {
    return null;
  }
  const params = {
    work_set_check_group_id: workSetCheckGroupId,
  };
  if (projectId != null && projectId !== "") {
    params.project_id = projectId;
  }
  try {
    const res = await employeeTasksAPI.readSchemaId(params);
    const id = res?.schema_id;
    return id != null ? id : null;
  } catch (e) {
    return null;
  }
};
